import {
    AbTabsType,
    AddCampaignAttachmentAction,
    CampaignBackendErrors,
    CampaignCreatorActionTypes,
    CampaignErrorNames,
    CampaignErrors,
    CheckEnteredDomainStartAction,
    ContentBackendErrorsType,
    CountRecipientsStartAction,
    EditCampaignTemplateAction,
    EmailPartsType,
    LoadCampaignSimpleStatsStartAction,
    LoadCampaignStartAction,
    RemoveCampaignAttachmentAction,
    SaveCampaignStartAction,
    SaveInMyTemplatesAction,
    SelectTemplateAction,
    SelectedTemplateType,
    StartRemoveAbTabsAction,
    emailLimitBackendErrors,
} from '@/store/campaignCreator/types';
import {
    Campaign,
    CampaignRecipient,
    CampaignStatus,
    CampaignTemplate,
    CampaignType,
    ContactStatus,
    EmailTransactionalMessage,
    MessageAttachment,
    Sender,
    TemplateScope,
    TemplateType,
    TransactionalRecipient,
} from '@dev/ee-api/ee-api-models';
import { CampaignsPaths, SettingsPaths } from '@/services/dictionaries/Paths';
import {
    CheckEnteredDomainError,
    CheckEnteredDomainSuccess,
    CountRecipientsError,
    CountRecipientsSuccess,
    LoadAttachmentFinish,
    LoadAttachmentStart,
    LoadCampaignError,
    LoadCampaignSimpleStatsError,
    LoadCampaignSimpleStatsSuccess,
    LoadCampaignSuccess,
    LoadCompletedCampaignsCountError,
    LoadCompletedCampaignsCountSuccess,
    LoadTemplateError,
    LoadTemplateStart,
    LoadTemplateSuccess,
    ModifyCampaignMap,
    OpenEditorDragAndDrop,
    OpenTemplateEditor,
    RemoveAbTabs,
    ReshardValidationFailed,
    SaveCampaignError,
    SaveCampaignSuccess,
    SelectTemplate,
    SetAbRecipients,
    SetAbTabs,
    SetActiveTemplateIndex,
    SetCampaignErrors,
    SetFirstErrorFocused,
    SetRecalcAbRecipients,
    SetSelectedTemplates,
    StartRemoveAbTabs,
    VerifyCampaign,
    getCampainCreatorStore,
} from '@/store/campaignCreator/actions';
import {
    ERROR_TOAST_DURATION,
    INDEX_PLACEHOLDER,
    INFO_TOAST_DURATION,
    SUCCESS_TOAST_DURATION,
} from '@/services/constants';
import { HideDialog, ShowDialog, UpdateDialog, getDialogStore } from '@/store/dialog/actions';
import { HideMediaManager, getMediaManagerStore } from '@/store/mediaManager/actions';
import { all, call, delay, fork, put, race, select, spawn, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { cloneDeep, divide, filter, find, forEach, includes, isEmpty, map, toString } from 'lodash-es';
import { getAccountStore, getGeneralInfo } from '@/store/account/actions';

import { AxiosError } from 'axios';
import React from 'react';
import { SubjectAndContent } from '@/components/CampaignCreator/SubjectAndContent';
import dayjs from '@/services/tools/dayjs';
import { getABTestNameByIndex } from '@/services/tools/campaigns';
import { getProductStore } from '@/store/products/actions';
import i18n from '@/../i18n';
import isEmail from 'validator/lib/isEmail';
import { loadSingleContact } from '@/services/ElasticApi';
import { push } from 'redux-first-history';
import { sendEventToGTM } from '@dev/ee-tools';
import { store } from '@shared-store/shared-store';
import { Logger } from '@/services/errors/appLogger';
import { sendEmail } from '../services/ApiCategories/emails';
import { isAxiosError } from './commonGenerators/errorToast';
import { countActiveContacts } from '../services/ApiCategories/contacts';
import { checkForDomainRestriction } from '../services/ApiCategories/domain';
import { getDefaultTemplateObj, loadTemplate, saveTemplate, updateTemplate } from '../services/ApiCategories/templates';
import {
    createCampaign,
    deleteFile,
    getCampaignSimpleStatsByName,
    getCampaignsCompletedCount,
    getSingleCampaignByName,
    updateCampaign,
    uploadFile,
} from '../services/ApiCategories/campaigns';
import {
    OpenEditorInCampaignCreator,
    SetTemplateFromGalleryToLoad,
    getRawEditorState,
} from '../store/raweditor/actions';
import { HideToast, ShowToast } from '../store/toast/actions';

export const INVALID_FILENAME_CHARACTERS_REGEX = /(\\|\/|:|\*|\?|"|<|>|\||\+|#|&|%|\s+)/g;

const ShowSuccessToast = (message: string): any =>
    ShowToast({
        severity: 'success',
        message: `Success: ${message}`,
        duration: SUCCESS_TOAST_DURATION,
        onClose: HideToast,
    });
const ShowErrorToast = (e): any =>
    ShowToast({
        severity: 'error',
        message: `Error: ${e.response?.data?.Error || e.message}`,
        duration: ERROR_TOAST_DURATION,
        onClose: HideToast,
    });
const ShowInfoToast = (message: string): any =>
    ShowToast({
        severity: 'info',
        message,
        duration: INFO_TOAST_DURATION,
        onClose: HideToast,
    });

function* getSelectedTemplates(templateName: string) {
    try {
        if (!templateName) return {};
        const template = yield call(loadTemplate, templateName, false);
        return {
            Sender: {
                FromName: template.FromName,
                From: [template.FromEmail],
                ReplyName: template.FromName,
                ReplyTo: [template.FromEmail],
            },
            Subject: template.Body.Subject,
            isFromGallery: false,
            templateType: template.TemplateType,
        };
    } catch (e) {
        yield put(ShowErrorToast(e));
    }
}

const checkCampaignSender = (campaign: Campaign): Campaign => {
    const templatesWithCheckedSender = map(campaign.CampaignTemplates, (template) => ({
        ...template,
        Sender: {
            ...template.Sender,
            From: [template?.Sender?.From?.[0]],
            ReplyTo: [template?.Sender?.ReplyTo?.[0]],
        },
    }));

    return {
        ...campaign,
        CampaignTemplates: templatesWithCheckedSender,
    };
};

function* loadCampaignByName(action: LoadCampaignStartAction): any {
    try {
        const { name } = action.payload;

        const responseCampaignByName = yield call(getSingleCampaignByName, name);
        const checkedCampaign = checkCampaignSender(responseCampaignByName.data);
        yield put(LoadCampaignSuccess(checkedCampaign));
        const campaignTemplates = yield all(
            responseCampaignByName?.data?.CampaignTemplates?.reduce(
                (acc: CampaignTemplate[], template: CampaignTemplate) =>
                    !template.TemplateName ? acc : [...acc, call(getSelectedTemplates, template.TemplateName)],
                []
            )
        );
        yield put(SetSelectedTemplates(campaignTemplates));
    } catch (e) {
        yield put(LoadCampaignError());
        yield put(ShowErrorToast(e));
    }
}

const hasVerifyErrors = (errors: CampaignErrors): boolean =>
    Object.keys(errors).findIndex((key) => key !== CampaignErrorNames.IS_DRAFT && errors[key] === true) > -1;

const getEmailParts = (email: string): EmailPartsType => {
    const index = email.indexOf('@');
    const emailDomain = email.substring(index + 1);
    const emailUsername = email.substring(0, index);

    return { emailDomain, emailUsername };
};

interface CheckTargetsErrorsProps {
    targets: CampaignRecipient;
    templatesVersionsCount: number;
    isOutOfRecipientsLimit: boolean;
    recipientsCount: number;
    campaignType: CampaignType;
}

const checkTargetsErrors = ({
    targets,
    templatesVersionsCount,
    isOutOfRecipientsLimit,
    recipientsCount,
    campaignType,
}: CheckTargetsErrorsProps): {
    noTargetsSelectedError?: boolean;
    outOfRecipientsLimitError?: boolean;
    noRecipientsError?: boolean;
    notEnoughRecipientsError?: boolean;
} => {
    const minimumRecipientsPerVersion = 2;
    const noTargets = targets?.ListNames.length === 0 && targets?.SegmentNames.length === 0;
    const isNotAutomation = campaignType !== CampaignType.Automated;

    if (noTargets) {
        return {
            noTargetsSelectedError: true,
        };
    }
    if (!recipientsCount && isNotAutomation) {
        return {
            noRecipientsError: true,
        };
    }
    if (isOutOfRecipientsLimit && isNotAutomation) {
        return {
            outOfRecipientsLimitError: true,
        };
    }
    if (
        isNotAutomation &&
        templatesVersionsCount >= 2 &&
        recipientsCount < templatesVersionsCount * minimumRecipientsPerVersion
    ) {
        return {
            notEnoughRecipientsError: true,
        };
    }
    return {};
};

const isUTMValueInvalid = (str: string): boolean => !str || str.indexOf(' ') >= 0;
const isCampaignNameToShort = (str: string): boolean => !str || str.length <= 4;

const verifyCampaignContent = (
    campaign: Campaign,
    errors: CampaignErrors,
    selectedTemplate: SelectedTemplateType[],
    contentBackendErrors?: ContentBackendErrorsType
): {
    contentTabIndex?: number;
} => {
    let contentTabIndex;

    const setContentTabIndex = (index: number): void => {
        if (contentTabIndex > -1) {
            return;
        }
        contentTabIndex = index;
    };

    campaign.CampaignTemplates?.forEach((template, index) => {
        if (!template.Body?.Subject) {
            errors[CampaignErrorNames.CAMPAIGN_TEMPLATES_BODY_SUBJECT.replace(INDEX_PLACEHOLDER, `${index}`)] = true;
            errors[CampaignErrorNames.SUBJECT_AND_CONTENT] = true;
            setContentTabIndex(index);
        }

        if (!template.Sender?.From[0]) {
            errors[CampaignErrorNames.FROM_EMAIL.replace(INDEX_PLACEHOLDER, `${index}`)] = true;
            errors[CampaignErrorNames.SUBJECT_AND_CONTENT] = true;
            setContentTabIndex(index);
        }
        if (!template.Sender?.ReplyTo[0]) {
            errors[CampaignErrorNames.REPLY_TO_EMAIL.replace(INDEX_PLACEHOLDER, `${index}`)] = true;
            errors[CampaignErrorNames.SUBJECT_AND_CONTENT] = true;
            setContentTabIndex(index);
        }
        if (!template.TemplateName) {
            errors[CampaignErrorNames.NO_EMAIL_TEMPLATE.replace(INDEX_PLACEHOLDER, `${index}`)] = true;
            errors[CampaignErrorNames.SUBJECT_AND_CONTENT] = true;
            setContentTabIndex(index);
        }
    });

    const emailSizeError = contentBackendErrors?.[CampaignBackendErrors.TEMPLATE_SIZE_EXCEEDS_LIMIT_SEND_CAMPAIGN];

    if (!isEmpty(emailSizeError)) {
        forEach(emailSizeError, (elementIndex) => {
            errors[CampaignErrorNames.TEMPLATE_SIZE.replace(INDEX_PLACEHOLDER, `${elementIndex}`)] = true;
            errors[CampaignErrorNames.SUBJECT_AND_CONTENT] = true;
            setContentTabIndex(elementIndex);
        });
    }

    const fromDomainError = contentBackendErrors?.[CampaignBackendErrors.FROM_EMAIL_NOT_ASSOCIATED];

    if (!isEmpty(fromDomainError)) {
        forEach(fromDomainError, (elementIndex) => {
            errors[CampaignErrorNames.FROM_EMAIL_NOT_ASSOCIATED.replace(INDEX_PLACEHOLDER, `${elementIndex}`)] = true;
            errors[CampaignErrorNames.SUBJECT_AND_CONTENT] = true;
            setContentTabIndex(elementIndex);
        });
    }

    const replyToDomainError = contentBackendErrors?.[CampaignBackendErrors.REPLY_TO_NOT_ASSOCIATED];

    if (!isEmpty(replyToDomainError)) {
        forEach(replyToDomainError, (elementIndex) => {
            errors[CampaignErrorNames.REPLY_TO_NOT_ASSOCIATED.replace(INDEX_PLACEHOLDER, `${elementIndex}`)] = true;
            errors[CampaignErrorNames.SUBJECT_AND_CONTENT] = true;
            setContentTabIndex(elementIndex);
        });
    }

    return { contentTabIndex };
};

const verifyCampaignUTM = (
    campaign: Campaign,
    errors: CampaignErrors
): {
    utmTabIndex?: number;
} => {
    let utmTabIndex;

    const setUtmTabIndex = (index: number): void => {
        if (utmTabIndex > -1) {
            return;
        }
        utmTabIndex = index;
    };

    campaign.CampaignTemplates?.forEach((template, index) => {
        if (template.UTM && template.UTM?.UtmSource !== null) {
            if (isUTMValueInvalid(template.UTM.UtmSource)) {
                errors[CampaignErrorNames.CAMPAIGN_TEMPLATES_UTM_SOURCE.replace(INDEX_PLACEHOLDER, `${index}`)] = true;
                errors[CampaignErrorNames.SETTING_AND_TRACKING] = true;
                setUtmTabIndex(index);
            }
            if (isUTMValueInvalid(template.UTM.UtmMedium)) {
                errors[CampaignErrorNames.CAMPAIGN_TEMPLATES_UTM_MEDIUM.replace(INDEX_PLACEHOLDER, `${index}`)] = true;
                errors[CampaignErrorNames.SETTING_AND_TRACKING] = true;
                setUtmTabIndex(index);
            }
            if (isUTMValueInvalid(template.UTM.UtmCampaign)) {
                errors[CampaignErrorNames.CAMPAIGN_TEMPLATES_UTM_CAMPAIGN.replace(INDEX_PLACEHOLDER, `${index}`)] =
                    true;
                errors[CampaignErrorNames.SETTING_AND_TRACKING] = true;
                setUtmTabIndex(index);
            }
            if (campaign.Type === CampaignType.AbSplit && isUTMValueInvalid(template.UTM.UtmContent)) {
                errors[CampaignErrorNames.CAMPAIGN_TEMPLATES_UTM_CONTENT.replace(INDEX_PLACEHOLDER, `${index}`)] = true;
                errors[CampaignErrorNames.SETTING_AND_TRACKING] = true;
                setUtmTabIndex(index);
            }
        }
    });

    return { utmTabIndex };
};

const verifySettings = (campaign: Campaign): { winnerSelectionHasError?: boolean } => {
    if (campaign.SplitOptions.DetermineWinnerAfterXMinutes < 60 && campaign.Type === CampaignType.AbSplit) {
        return {
            winnerSelectionHasError: true,
        };
    }
    return {};
};

const contentTabHasError = (errors: CampaignErrors, index: number): boolean => {
    const contentTabErrors = [
        CampaignErrorNames.FROM_EMAIL,
        CampaignErrorNames.REPLY_TO_EMAIL,
        CampaignErrorNames.CAMPAIGN_TEMPLATES_BODY_SUBJECT,
        CampaignErrorNames.TEMPLATE_SIZE,
        CampaignErrorNames.NO_EMAIL_TEMPLATE,
        CampaignErrorNames.FROM_EMAIL_NOT_ASSOCIATED,
        CampaignErrorNames.REPLY_TO_NOT_ASSOCIATED,
    ];

    return !!find(contentTabErrors, (tabError) => !!errors?.[tabError.replace(INDEX_PLACEHOLDER, `${index}`)]);
};

const setContentTabsErrorState = (errors: CampaignErrors, abTabs: AbTabsType[]): void => {
    const abTabsWithErrors = map(
        abTabs,
        (tab, index): AbTabsType => ({
            ...tab,
            hasError: contentTabHasError(errors, index),
        })
    );
    store.dispatch(SetAbTabs(abTabsWithErrors));
};

const getBackendErrors = (backendErrorObject: Error | AxiosError): ContentBackendErrorsType | Record<string, never> => {
    const contentBackendErrors = {};
    const backendErrorParams =
        (isAxiosError(backendErrorObject) && backendErrorObject?.response?.data?.ErrorParams) || [];

    if (isEmpty(backendErrorParams)) {
        return contentBackendErrors;
    }

    forEach(backendErrorParams, (errors, templateIndex) => {
        forEach(errors, (singleError) => {
            const singleErrorArr = contentBackendErrors[singleError.MessageID] || [];
            singleErrorArr.push(templateIndex);
            contentBackendErrors[singleError.MessageID] = singleErrorArr;
        });
    });

    return contentBackendErrors;
};

function* verifyCampaign(isDraft?: boolean, backendErrorObject?: Error | AxiosError): any {
    const {
        campaign,
        recipientsCount,
        selectedTemplate,
        errors: previousErrors,
    } = yield select(getCampainCreatorStore);
    const { GeneralInfo, Usage } = yield select(getAccountStore);
    const { dashboards } = yield select(getProductStore);
    const isOutOfRecipientsLimit = checkIsOutOfRecipientsLimit(GeneralInfo, Usage, dashboards, recipientsCount);

    const errors: CampaignErrors = {
        [CampaignErrorNames.IS_DRAFT]: isDraft ?? previousErrors?.[CampaignErrorNames.IS_DRAFT],
        [CampaignErrorNames.TARGETS]: false,
        [CampaignErrorNames.SUBJECT_AND_CONTENT]: false,
        [CampaignErrorNames.SETTING_AND_TRACKING]: false,
    };

    const backendError = isAxiosError(backendErrorObject)
        ? backendErrorObject?.response?.data?.Error
        : backendErrorObject?.message;

    /* DRAFT VALIDATION CHECKS ONLY START */
    if (backendError?.startsWith(CampaignBackendErrors.NAME_ALREADY_EXISTS)) {
        errors[CampaignErrorNames.SETTING_AND_TRACKING] = true;
        errors[CampaignErrorNames.NAME] = true;
    }

    if (campaign.Name && isCampaignNameToShort(campaign.Name)) {
        errors[CampaignErrorNames.SETTING_AND_TRACKING] = true;
        errors[CampaignErrorNames.SHORTNAME] = true;
    }
    if (isDraft) {
        return { errors };
    }

    /* END DRAFT VALIDATION CHECKS ONLY */

    const { noTargetsSelectedError, outOfRecipientsLimitError, noRecipientsError, notEnoughRecipientsError } =
        checkTargetsErrors({
            targets: campaign.Targets,
            templatesVersionsCount: campaign?.CampaignTemplates?.length ?? 0,
            isOutOfRecipientsLimit,
            recipientsCount,
            campaignType: campaign.Type,
        });

    const contentBackendErrors = getBackendErrors(backendErrorObject);

    const { contentTabIndex } = verifyCampaignContent(campaign, errors, selectedTemplate, contentBackendErrors);
    const { utmTabIndex } = verifyCampaignUTM(campaign, errors);

    const { winnerSelectionHasError } = verifySettings(campaign);

    if (winnerSelectionHasError) {
        errors[CampaignErrorNames.SETTING_AND_TRACKING] = true;
        errors[CampaignErrorNames.WINNER_SELECTION_UNIT] = true;
    }

    if (noTargetsSelectedError) {
        errors[CampaignErrorNames.TARGETS] = true;
        errors[CampaignErrorNames.NO_TARGETS_SELECTED] = true;
    }

    if (noRecipientsError) {
        errors[CampaignErrorNames.TARGETS] = true;
        errors[CampaignErrorNames.NO_RECIPIENTS] = true;
    }

    if (includes(emailLimitBackendErrors, backendError)) {
        errors[CampaignErrorNames.TARGETS] = true;
        errors[CampaignErrorNames.OUT_OF_RECIPIENTS_LIMIT] = true;
    }

    if (outOfRecipientsLimitError) {
        errors[CampaignErrorNames.TARGETS] = true;
        errors[CampaignErrorNames.OUT_OF_RECIPIENTS_LIMIT] = true;
    }

    if (notEnoughRecipientsError) {
        errors[CampaignErrorNames.TARGETS] = true;
        errors[CampaignErrorNames.NOT_ENOUGH_RECIPIENTS] = true;
    }

    return { errors, contentTabIndex, utmTabIndex };
}

function* scrollToFirstError(): any {
    const { errors, errorsSubscribers, firstErrorFocused } = yield select(getCampainCreatorStore);
    if (firstErrorFocused || !errors || !hasVerifyErrors(errors) || !errorsSubscribers.length) {
        return;
    }

    const order = errorsSubscribers.find((subscriber) => !!errors[subscriber.errorName])?.order;
    const firstErrorElement = errorsSubscribers.find(
        (subscriber): boolean =>
            !!errors[subscriber.errorName] && !!document.getElementById(subscriber.id) && subscriber.order === order
    );

    if (firstErrorElement) {
        setTimeout((): void => {
            document.getElementById(firstErrorElement.id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
        yield put(SetFirstErrorFocused());
    }
}

const checkIsOutOfRecipientsLimit = (GeneralInfo, Usage, dashboards, recipientsCount): boolean => {
    const { DailySendLimit, EmailCredits, IsSub, IsTrial } = GeneralInfo;
    const emailsSentToday = dayjs(Usage?.[0]?.Date).isToday() ? Usage?.[0]?.RecipientCount : 0;
    let hasEmailLimit = false;
    let emailLimit = 0;

    if (IsSub) {
        hasEmailLimit = DailySendLimit > 0;
        emailLimit = DailySendLimit;
    } else if (IsTrial) {
        const trialEmailLimit = !EmailCredits && !emailsSentToday ? EmailCredits : DailySendLimit;
        hasEmailLimit = true;
        emailLimit = trialEmailLimit || 100;
    } else {
        // hasEmailLimit =
        //     dashboards?.[0]?.ActivePlanDetails?.IsSubscriptionBased &&
        //     !dashboards?.[0]?.ActivePlanDetails?.UsingEmailCredits
        //         ? false
        //         : true;
        hasEmailLimit = false;
        emailLimit = EmailCredits || 0;
    }
    // return false;
    return hasEmailLimit && recipientsCount > 0 && emailLimit < recipientsCount;
};

function* reverifyCampaign(): any {
    const { abTabs, errors: previousErrors } = yield select(getCampainCreatorStore);
    const { errors } = yield call(verifyCampaign, previousErrors?.[CampaignErrorNames.IS_DRAFT], null);
    if (hasVerifyErrors(errors)) {
        setContentTabsErrorState(errors, abTabs);
        yield put(SetCampaignErrors(errors));
        yield put(SetFirstErrorFocused());
        return;
    }
    yield put(SetCampaignErrors({}));
    setContentTabsErrorState({}, abTabs);
}

//TODO: Ask for extended type.

const prepareCampaignToSave = (campaign: Campaign, isDraft: boolean): Campaign => {
    const campaignObject = cloneDeep(campaign);
    campaignObject.Status = isDraft ? CampaignStatus.Draft : CampaignStatus.Active;

    return campaignObject;
};

//actual saveing of the campaign and redirecting if necessary, to lower complexity
function* submitCampaignAndRedirect(action: SaveCampaignStartAction) {
    const { campaign, isNew, name } = yield select(getCampainCreatorStore);
    const { isDraft, openDomainValidationScreen, redirectURL } = action.payload;
    const campaignObject = prepareCampaignToSave(campaign, isDraft);

    //case: when we open campaigns edit mode and then we save as draft
    if (isNew || (campaign.Status !== CampaignStatus.Draft && isDraft)) {
        yield call(createCampaign, campaignObject);
    } else {
        yield call(updateCampaign, campaignObject, name);
    }
    yield put(SaveCampaignSuccess({ meta: action.meta }));

    sendEventToGTM('CampaignSent', {
        numberOfRecipients: campaignObject?.RecipientsCount,
        timestamp: campaignObject?.DateAdded,
    });

    if (isDraft) {
        yield put(ShowSuccessToast(i18n.t('campaignsavedasdraft')));
    }

    if (redirectURL) {
        return yield put(push(redirectURL));
    }

    if (isDraft) {
        if (openDomainValidationScreen) {
            yield put(push(SettingsPaths.DomainsDomainVerification));
            return;
        }
        yield put(push(CampaignsPaths.DratfsTab));
    }
}

function* saveCampaign(action: SaveCampaignStartAction): any {
    const { abTabs } = yield select(getCampainCreatorStore);
    const { isDraft } = action.payload;
    try {
        const { errors, contentTabIndex, utmTabIndex } = yield call(verifyCampaign, isDraft, null);

        if (hasVerifyErrors(errors)) {
            setContentTabsErrorState(errors, abTabs);
            yield put(
                SetCampaignErrors(errors, {
                    activeTemplateIndex: contentTabIndex,
                    activeUtmTabIndex: utmTabIndex,
                })
            );
            yield put(HideDialog());
            yield spawn(scrollToFirstError);
            yield put(SaveCampaignError());
            return;
        }

        yield put(SetCampaignErrors({}));
        //actual saving of the campaing
        yield call(submitCampaignAndRedirect, action);
    } catch (e) {
        // Account need to be resharded - modal with option selection is being displayed.
        if (e?.response?.data?.MessageID === CampaignBackendErrors.CAMPAIGN_RESHARD_REQUIRED_ERROR_MSG) {
            return yield put(ReshardValidationFailed({ meta: action.meta }));
        }
        yield put(HideDialog());
        const message = isAxiosError(e) ? e?.response?.data?.Error : e?.message;
        Logger.error(message);

        const { errors, contentTabIndex } = yield call(verifyCampaign, isDraft, e);
        if (contentTabIndex) {
            yield put(SetActiveTemplateIndex(contentTabIndex));
        }
        if (hasVerifyErrors(errors)) {
            setContentTabsErrorState(errors, abTabs);
            yield put(SetCampaignErrors(errors));
            yield spawn(scrollToFirstError);
            yield put(SaveCampaignError());
            return;
        }
        yield put(SetCampaignErrors(errors));
        yield put(SaveCampaignError());
        yield put(ShowErrorToast(e));
    }
}

function* loadSelectedTemplate(action: SelectTemplateAction): any {
    const { templateName, isPublic, isFromGallery = false } = action.payload;
    const { activeTemplateIndex, errors } = yield select(getCampainCreatorStore);
    const hasNoTemplateErrorName = CampaignErrorNames.NO_EMAIL_TEMPLATE.replace(
        INDEX_PLACEHOLDER,
        `${activeTemplateIndex}`
    );
    const hasNoTemplateError = !!errors?.[hasNoTemplateErrorName];

    try {
        yield put(LoadTemplateStart(templateName, activeTemplateIndex));
        const template = yield call(loadTemplate, templateName, isPublic);
        yield put(LoadTemplateSuccess(template, activeTemplateIndex, isFromGallery));
        if (hasNoTemplateError) {
            yield put(VerifyCampaign());
        }
    } catch (e) {
        yield put(LoadTemplateError(activeTemplateIndex));
        yield put(ShowErrorToast(e));
    }
}

function* getRecipientsCount(action: CountRecipientsStartAction): any {
    yield delay(5000); // DEBOUNCE FUNCTIONALITY - DO NOT REMOVE
    try {
        const { isAllContactsSelected } = action.payload;
        const { campaign } = yield select(getCampainCreatorStore);
        const targets = [...(campaign?.Targets?.ListNames || []), ...(campaign?.Targets?.SegmentNames || [])];

        let count: number;
        if (!isEmpty(targets)) {
            if (isAllContactsSelected) {
                count = yield call(countActiveContacts, [], true);
            } else {
                count = yield call(countActiveContacts, targets, false);
            }
            yield put(CountRecipientsSuccess(count));
        } else {
            yield put(CountRecipientsSuccess(0));
        }
    } catch (e) {
        yield put(CountRecipientsError());
        yield put(ShowErrorToast(e));
    }
}

function* startGetRecepientCountSaga(action: CountRecipientsStartAction) {
    yield race([call(getRecipientsCount, action), take(CampaignCreatorActionTypes.CLEAR_PICKED_CAMAPAIGN)]);
}

const checkFileSize = (emailSizeLimit: number, fileSize: number, attachments: Array<MessageAttachment>): boolean => {
    let attachmentsSize = 0; // in bytes

    forEach(attachments, (attachment) => {
        attachmentsSize += attachment.Size;
    });

    attachmentsSize += fileSize; // fileSize is in bytes

    return attachmentsSize / 1048576 < emailSizeLimit;
};

function* addCampaignAttachment(action: AddCampaignAttachmentAction): any {
    const { file, templateIndex } = action.payload;
    const generalInfo = yield select(getGeneralInfo);
    const emailSizeLimit = generalInfo?.EmailSizeLimit;

    const { campaign } = yield select(getCampainCreatorStore);
    const attachments = campaign?.CampaignTemplates?.[templateIndex]?.Attachments || [];

    const allAtachments = [];
    forEach(campaign?.CampaignTemplates, (template) => {
        !isEmpty(template.Attachments) && allAtachments.push(...template.Attachments);
    });

    try {
        yield put(LoadAttachmentStart());
        let fileName = file.name;
        const invalidChars = ['\\', '/', ':', '*', '?', '"', '<', '>', '|', '+', '#', '&', '%', ' '];
        if (invalidChars.some((char) => file.name.includes(char))) {
            fileName = file.name.replace(INVALID_FILENAME_CHARACTERS_REGEX, '_');
        }

        const isFileNameExists = !!find(allAtachments, (attachment) => attachment.Name === fileName);
        const canFileBeUploaded = checkFileSize(emailSizeLimit, file.size, attachments) && !isFileNameExists;

        if (canFileBeUploaded) {
            const newFile: File = new File([file], fileName, { type: file.type });
            const uploadedFile = yield call(uploadFile, { file: newFile });

            const newAttachment: MessageAttachment = {
                // only Name is required to send/save campaign, but BinaryContent is required, so it has to be passed as null
                BinaryContent: null,
                Name: uploadedFile.FileName,
                ContentType: uploadedFile.ContentType,
                Size: uploadedFile.Size,
            };
            const updatedAttachments = [...attachments, newAttachment];

            if (file.name !== uploadedFile.FileName) {
                yield put(
                    ShowToast({
                        severity: 'info',
                        message: i18n.t('filenameunallowedcharacters'),
                        duration: INFO_TOAST_DURATION,
                        onClose: HideToast,
                        width: 750,
                    })
                );
            }
            const modifyMap = new Map<string, any>();
            modifyMap.set(`CampaignTemplates[${templateIndex}].Attachments`, updatedAttachments);
            yield put(ModifyCampaignMap(modifyMap));
            yield put(LoadAttachmentFinish());
        } else {
            if (isFileNameExists) {
                yield put(
                    ShowToast({
                        severity: 'error',
                        message: i18n.t('cantattachfileswiththesamename'),
                        duration: ERROR_TOAST_DURATION,
                        onClose: HideToast,
                        width: 700,
                    })
                );
                yield put(LoadAttachmentFinish());
            } else {
                yield put(
                    ShowToast({
                        severity: 'error',
                        message: i18n.t('filecannotbeadded'),
                        duration: ERROR_TOAST_DURATION,
                        onClose: HideToast,
                        width: 500,
                    })
                );
                yield put(LoadAttachmentFinish());
            }
        }
    } catch (e) {
        yield put(ShowErrorToast(e));
        yield put(LoadAttachmentFinish());
    }
}

function* removeCampaignAttachment(action: RemoveCampaignAttachmentAction): any {
    const { fileName, templateIndex } = action.payload;
    const { campaign } = yield select(getCampainCreatorStore);
    const attachments: MessageAttachment[] = campaign?.CampaignTemplates?.[templateIndex]?.Attachments || [];

    try {
        const updatedAttachments = filter(attachments, (attachment) => attachment?.Name !== fileName);
        yield call(deleteFile, fileName);
        const modifyMap = new Map<string, any>();
        modifyMap.set(`CampaignTemplates[${templateIndex}].Attachments`, updatedAttachments);
        yield put(ModifyCampaignMap(modifyMap));
    } catch (e) {
        yield put(ShowErrorToast(e));
    }
}

function* getCompletedCampaignsCount(): any {
    try {
        const count = yield call(getCampaignsCompletedCount);
        yield put(LoadCompletedCampaignsCountSuccess(count));
    } catch (e) {
        yield put(LoadCompletedCampaignsCountError());
        yield put(ShowErrorToast(e));
    }
}

function* checkEnteredDomain(action: CheckEnteredDomainStartAction): any {
    const { email } = action.payload;
    try {
        const { emailDomain } = getEmailParts(email);
        const response = yield call(checkForDomainRestriction, emailDomain);
        const isFreeDomain = response.data;
        yield put(CheckEnteredDomainSuccess(isFreeDomain));
    } catch (e) {
        yield put(CheckEnteredDomainError());
    }
}

function* getContactStatus(email: string) {
    try {
        const contact = yield call(loadSingleContact, email);
        return contact.Status;
    } catch (e) {
        return 'Error';
    }
}

function* verifySendTest(recipients?: string[], backendError?: string) {
    const errors: CampaignErrors = {};
    const { EmailSizeLimit } = yield select(getGeneralInfo);

    if (!isEmpty(recipients)) {
        const emailsInvalid = !!find(recipients, (email) => !isEmail(email));
        if (emailsInvalid) {
            errors[CampaignErrorNames.SEND_TEST_INVALID_EMAILS] = true;
        } else {
            const recipientsStatuses = yield all(recipients.map((email) => call(getContactStatus, email)));
            const emailsStatusInvalid = !!find(
                recipientsStatuses,
                (status) => status !== ContactStatus.Active && status !== ContactStatus.Engaged
            );
            errors[CampaignErrorNames.SEND_TEST_INVALID_EMAILS_STATUS] = emailsStatusInvalid;
        }
    }

    if (includes(emailLimitBackendErrors, backendError)) {
        errors[CampaignErrorNames.NO_EMAIL_CREDITS] = true;
    }

    if (
        backendError ===
        CampaignBackendErrors.TEMPLATE_SIZE_EXCEEDS_LIMIT_SEND_TEST.replace(INDEX_PLACEHOLDER, `${EmailSizeLimit}`)
    ) {
        errors[CampaignErrorNames.SEND_TEST_TEMPLATE_SIZE] = true;
    }

    return { errors };
}

function* sendTest(): any {
    const { campaign, activeTemplateIndex, defaultCampaign, sendTestConfig } = yield select(getCampainCreatorStore);
    const { to, fromName, subject, previewText, replyEmail, replyName } = sendTestConfig;
    const campaignObject = cloneDeep(campaign);
    const campaignTemplate = campaignObject.CampaignTemplates[activeTemplateIndex];
    const defaultCampaignFromEmail = defaultCampaign?.Sender?.From;
    campaignTemplate.Body.Subject = subject;
    campaignTemplate.Body.PreHeader = previewText;
    const recipientsArray = to.split(';').map((item) => item.trim());

    const Sender: Sender = {
        From: campaignTemplate.Sender.From ?? defaultCampaignFromEmail,
        ReplyTo: [replyEmail ?? defaultCampaignFromEmail],
        FromName: fromName,
        ReplyName: replyName,
    };

    const Recipients: TransactionalRecipient = {
        To: recipientsArray,
    };

    const email: EmailTransactionalMessage = {
        Body: campaignTemplate.Body,
        Sender,
        Recipients,
        TemplateName: campaignTemplate.TemplateName,
        Attachments: campaignTemplate.Attachments,
    };

    try {
        yield put(UpdateDialog({ isLoading: true }));
        const { errors } = yield call(verifySendTest, recipientsArray, null);
        yield put(SetCampaignErrors(errors));
        if (
            !errors[CampaignErrorNames.SEND_TEST_INVALID_EMAILS] &&
            !errors[CampaignErrorNames.SEND_TEST_INVALID_EMAILS_STATUS]
        ) {
            yield call(sendEmail, email);
            yield put(ShowInfoToast(i18n.t('testemailsent')));
            yield put(HideDialog());
        } else {
            yield put(UpdateDialog({ isLoading: false }));
        }
    } catch (e) {
        yield put(UpdateDialog({ isLoading: false }));
        const { errors } = yield call(verifySendTest, null, e?.response?.data?.Error);
        yield put(SetCampaignErrors(errors));
        if (isEmpty(errors)) {
            yield put(ShowErrorToast(e));
        }
    }
}

function* continueWithHTMLTemplate(): any {
    const {
        templateName,
        isNewTemplate,
        templateObject,
        isTemplateFromGallery = false,
    } = yield select(getRawEditorState);
    const { selectedTemplate, activeTemplateIndex } = yield select(getCampainCreatorStore);
    const activeSelectedTemplate = selectedTemplate?.[activeTemplateIndex];
    const isFromGallery = activeSelectedTemplate?.isFromGallery || isTemplateFromGallery;
    let savedTemplate;

    try {
        if (isNewTemplate && !isFromGallery) {
            const defaultTemplate = yield call(getDefaultTemplateObj, TemplateType.RawHTML);
            const templateToSave = defaultTemplate.data;
            templateToSave.Name = templateName;
            templateToSave.TemplateScope = TemplateScope.Draft;
            savedTemplate = yield call(saveTemplate, templateToSave);
        }
        if (isNewTemplate && isFromGallery) {
            const templateToSave = templateObject;
            templateToSave.TemplateScope = TemplateScope.Draft;
            savedTemplate = yield call(saveTemplate, templateToSave);
        }
        if (!isNewTemplate) {
            const templateToUpdate = cloneDeep(templateObject);
            templateToUpdate.TemplateScope = TemplateScope.Draft;
            savedTemplate = yield call(updateTemplate, templateName, templateToUpdate);
        }
        yield put(SelectTemplate(templateName, savedTemplate.isPublic));
    } catch (e) {
        yield put(ShowErrorToast(e));
    }
}

function* editCampaignTemplate(action: EditCampaignTemplateAction): any {
    const { templateName, templateType, isPublic, isEditedFromGallery } = action.payload;
    const { selectedTemplate, activeTemplateIndex } = yield select(getCampainCreatorStore);
    const activeSelectedTemplate = selectedTemplate?.[activeTemplateIndex];

    const type = activeSelectedTemplate?.templateType ?? templateType;

    try {
        if (type === TemplateType.RawHTML) {
            if (!!templateName) {
                yield put(SetTemplateFromGalleryToLoad(templateName, false, isPublic));
            }
            yield put(OpenEditorInCampaignCreator());
        }
        if (type === TemplateType.DragDropEditor) {
            yield put(OpenEditorDragAndDrop(templateName, isPublic, isEditedFromGallery));
        }
        if (type === TemplateType.TemplateEditor) {
            yield put(OpenTemplateEditor(templateName));
        }
    } catch (e) {
        yield put(ShowErrorToast(e));
    }
}

function* saveInMyTemplates(action: SaveInMyTemplatesAction): any {
    const { templateName, fromEmail, fromName, subject } = action.payload.params;
    const { campaign, activeTemplateIndex } = yield select(getCampainCreatorStore);
    const errors: CampaignErrors = {};
    const campaignObject = cloneDeep(campaign);
    const campaignTemplate = campaignObject.CampaignTemplates[activeTemplateIndex];

    try {
        yield put(UpdateDialog({ isLoading: true }));
        const template = yield call(loadTemplate, campaignTemplate.TemplateName, campaignTemplate.isPublic);

        const templateObject = {
            ...template,
            TemplateScope: TemplateScope.Private,
            Name: templateName,
            TemplateName: templateName,
            FromEmail: fromEmail,
            FromName: fromName,
            Body: {
                Subject: subject,
                Parts: template.Body.Parts,
            },
        };

        yield call(saveTemplate, templateObject);
        yield put(ShowInfoToast(i18n.t('templatesaved')));
        yield put(HideDialog());
    } catch (e) {
        yield put(UpdateDialog({ isLoading: false }));
        if (e?.response?.data?.Error === CampaignBackendErrors.TEMPLATE_NAME_IN_USE) {
            errors[CampaignErrorNames.TEMPLATE_NAME_IN_USE] = true;
        }
        yield put(SetCampaignErrors(errors));
    }
}

function* removeTab(action: StartRemoveAbTabsAction): any {
    const { id } = action.payload;
    const { abTabs } = yield select(getCampainCreatorStore);
    const filtered: AbTabsType[] = abTabs.filter((item: AbTabsType) => item.id !== toString(id));

    function calcNewId(item: AbTabsType, index: number): AbTabsType {
        return {
            ...item,
            id: toString(index),
            tabTitle: `${i18n.t('version')} ${getABTestNameByIndex(index)}`,
            onDelete: () => store.dispatch(StartRemoveAbTabs(index)),
            body: <SubjectAndContent index={index} />,
        };
    }

    const onClickDelete = (): void => {
        const newAbTabs = map(filtered, calcNewId);
        store.dispatch(SetAbTabs(newAbTabs));
        store.dispatch(SetAbRecipients());
        store.dispatch(RemoveAbTabs(id));
        if (id === filtered.length) {
            store.dispatch(SetActiveTemplateIndex(id - 1));
        }
        const hasAbTabsErrors = !!find(newAbTabs, (tab) => tab.hasError);
        hasAbTabsErrors && store.dispatch(VerifyCampaign());
        store.dispatch(ShowInfoToast(i18n.t('versiondeleted')));
    };

    try {
        yield put(
            ShowDialog({
                title: i18n.t('deleteversion'),
                cancelLabel: i18n.t('cancel'),
                body: i18n.t('areyousureyouwanttodeletecampaign'),
                destructiveLabel: i18n.t('delete'),
                onClickDestructive: onClickDelete,
            })
        );
    } catch (e) {
        yield put(ShowErrorToast(e));
    }
}

function* loadCampaignSimpleStatsByName(action: LoadCampaignSimpleStatsStartAction): any {
    try {
        const { name, from } = action.payload;
        const responseCampaignByName = yield call(getCampaignSimpleStatsByName, { name, from });
        yield put(LoadCampaignSimpleStatsSuccess(responseCampaignByName.data as Campaign));
    } catch (e) {
        yield put(LoadCampaignSimpleStatsError());
        yield put(ShowErrorToast(e));
    }
}

function* setAbRecipients(): any {
    const { abTabs, campaign } = yield select(getCampainCreatorStore);

    try {
        const totalCount = campaign?.RecipientsCount;
        const testTemplatesCount = abTabs.length;
        const testTotal = Math.ceil(0.1 * totalCount);
        const perTestTotal = Math.ceil(divide(testTotal, testTemplatesCount));
        const perTestTotalPercent = Math.ceil(divide(10, testTemplatesCount));
        const winnerTotalPercent = Math.ceil(100 - perTestTotalPercent * testTemplatesCount);

        const abRecipientsData = [];
        let winnerTotal = totalCount;
        map(abTabs, () => {
            const recipientsCount = winnerTotal > 0 ? perTestTotal : 0;
            abRecipientsData.push({
                uniqueRecipients: recipientsCount,
                uniqueRecipientsPercent: !perTestTotalPercent ? 0 : perTestTotalPercent,
            });
            winnerTotal -= recipientsCount;
        });
        abRecipientsData.push({
            uniqueRecipients: !winnerTotal || winnerTotal <= 0 ? 0 : winnerTotal,
            uniqueRecipientsPercent: !winnerTotalPercent ? 0 : winnerTotalPercent,
            isWinner: true,
        });

        yield put(SetRecalcAbRecipients(abRecipientsData));
    } catch (e) {
        yield put(ShowErrorToast(e));
    }
}

function* onClose(): any {
    const { isOpened } = yield select(getDialogStore);
    const { isOpened: isMediaManagerOpened } = yield select(getMediaManagerStore);
    if (isOpened) {
        yield put(HideDialog());
    }
    if (isMediaManagerOpened) {
        yield put(HideMediaManager());
    }
}

function* watchLoadStart(): any {
    yield takeEvery(CampaignCreatorActionTypes.LOAD_START, loadCampaignByName);
    yield takeEvery(CampaignCreatorActionTypes.SAVE_START, saveCampaign);
    yield takeEvery(CampaignCreatorActionTypes.VERIFY_CAMPAIGN, reverifyCampaign);
    yield takeEvery(CampaignCreatorActionTypes.SELECT_TEMPLATE, loadSelectedTemplate);
    yield takeLatest(CampaignCreatorActionTypes.COUNT_RECIPIENTS_START, startGetRecepientCountSaga);
    yield takeEvery(CampaignCreatorActionTypes.ADD_ATTACHMENT, addCampaignAttachment);
    yield takeEvery(CampaignCreatorActionTypes.REMOVE_ATTACHMENT, removeCampaignAttachment);
    yield takeLatest(CampaignCreatorActionTypes.SET_CAMPAIGN_ERRORS_SUBSCRIBER, scrollToFirstError);
    yield takeLatest(CampaignCreatorActionTypes.LOAD_COMPLETED_CAMPAIGNS_COUNT_START, getCompletedCampaignsCount);
    yield takeEvery(CampaignCreatorActionTypes.CHECK_ENTERED_DOMAIN_START, checkEnteredDomain);
    yield takeLatest(CampaignCreatorActionTypes.SEND_TEST, sendTest);
    yield takeLatest(CampaignCreatorActionTypes.CONTINUE_WITH_HTML_TEMPLATE, continueWithHTMLTemplate);
    yield takeLatest(CampaignCreatorActionTypes.EDIT_CAMPAIGN_TEMPLATE, editCampaignTemplate);
    yield takeEvery(CampaignCreatorActionTypes.START_REMOVE_AB_TABS, removeTab);
    yield takeEvery(CampaignCreatorActionTypes.SAVE_IN_MY_TEMPLATES, saveInMyTemplates);
    yield takeLatest(CampaignCreatorActionTypes.LOAD_CAMPAIGN_SIMPLE_STATS_START, loadCampaignSimpleStatsByName);
    yield takeLatest(CampaignCreatorActionTypes.SET_AB_RECIPIENTS, setAbRecipients);
    yield takeLatest(CampaignCreatorActionTypes.CLEAR_PICKED_CAMAPAIGN, onClose);
}

const campaignCreatorSagas = [fork(watchLoadStart)];

export default campaignCreatorSagas;
